import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, Injector, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  AccordionComponent,
  AccordionItemComponent,
  Avatar,
  Dropdown,
  DropdownOption,
  EntityDetailLayoutComponent,
  ErrorStateComponent,
  FormField,
  Input,
  LoadingStateComponent,
  PageActionsDirective,
  ProfileSummaryCardComponent,
  type ProfileDangerAction,
  type ProfileQuickAction,
  ConfirmDialogService,
  ToastService,
  ZagButton,
  ZagIcon,
  getFieldError as getFieldErrorUtil,
  hasAuditLogAccess,
  SidebarNavStateService,
  wireAuditPanelAutoOpen,
} from '@zag/ui';
import { AuditLogPanelComponent } from '@zag/core/ui';
import { GroupsManagementService, type GroupMember, type GroupRole } from '../groups-management.service';
import { UsersManagementService } from '../users-management.service';
import { RolesManagementService } from '../roles-management.service';
import { forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { PermissionService } from '../permission.service';

@Component({
  selector: 'sc-group-edit',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    EntityDetailLayoutComponent,
    ProfileSummaryCardComponent,
    AccordionComponent,
    AccordionItemComponent,
    AuditLogPanelComponent,
    Avatar,
    Dropdown,
    FormField,
    Input,
    ZagButton,
    ZagIcon,
    LoadingStateComponent,
    ErrorStateComponent,
    PageActionsDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './group-edit.component.html',
  styleUrl: './group-edit.component.scss',
})
export class GroupEditComponent {
  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly currentLang = toSignal(this.translate.onLangChange, {
    initialValue: { lang: this.translate.currentLang, translations: {} },
  });
  private readonly groupsService = inject(GroupsManagementService);
  private readonly usersService = inject(UsersManagementService);
  private readonly rolesService = inject(RolesManagementService);
  private readonly permissionService = inject(PermissionService);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly sidebarNavState = inject(SidebarNavStateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  /** Whether the audit log panel is open */
  readonly auditPanelOpen = signal(false);
  readonly canAccessAuditLog = computed(() => hasAuditLogAccess(this.permissionService));

  /** Route params as signal for reactive updates */
  private readonly routeParams = toSignal(this.route.paramMap);

  // Group ID from route (null for create mode) - reactive
  readonly groupId = computed(() => this.routeParams()?.get('id') ?? null);

  // Check if creating new
  readonly isNew = computed(() => !this.groupId());

  // Use the existing service's selected group
  private readonly selectedGroup = this.groupsService.selectedGroup;

  // Loading state
  readonly isLoading = computed(() => {
    if (this.isNew()) return false;
    return this.groupsService.isLoadingSelected();
  });

  // Track save state for all operations
  protected readonly isSaving = signal(false);

  // Track when we're waiting for data refresh after save
  private readonly _pendingRefresh = signal(false);

  // Track previous data to detect when fresh data arrives
  private _previousDataHash = '';

  // Error state
  readonly error = computed(() => {
    this.currentLang();
    if (this.isNew()) return null;
    const groupError = this.groupsService.error();
    if (groupError) {
      return this.translate.instant('settings.groups.loadError');
    }
    return null;
  });

  readonly form = new FormGroup({
    name: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    description: new FormControl<string | null>(null),
  });

  /** Convert form values to signal for reactivity */
  private readonly formValues = toSignal(this.form.valueChanges, {
    initialValue: this.form.value,
  });

  // Track if form has been modified from the original
  private _originalFormValues: { name: string; description: string | null } = {
    name: '',
    description: null,
  };

  /** Current group name for profile card */
  readonly groupName = computed(() => {
    this.currentLang();
    const formValues = this.formValues();
    if (formValues.name) return formValues.name;

    const id = this.groupId();
    if (id) {
      const group = this.selectedGroup();
      return group?.name ?? this.translate.instant('settings.groups.loading');
    }
    return this.translate.instant('settings.groups.newGroup');
  });

  /** Group icon for profile card */
  readonly groupIcon = computed(() => 'groups');

  /** Profile subtitle */
  readonly groupSubtitle = computed(() => {
    this.currentLang();
    return this.translate.instant('settings.groups.entityType');
  });

  /** Quick actions for profile card */
  readonly quickActions = computed<ProfileQuickAction[]>(() => {
    this.currentLang();
    const isNewGroup = this.isNew();
    if (!this.canAccessAuditLog()) {
      return [];
    }

    return [
      {
        id: 'audit-log',
        label: this.translate.instant('common.activityLog'),
        icon: 'history',
        disabled: isNewGroup,
      },
    ];
  });

  /** Danger zone actions for profile card */
  readonly dangerActions = computed<ProfileDangerAction[]>(() => {
    this.currentLang();
    const group = this.selectedGroup();
    // Disable delete if group has members
    const inUse = group && group.memberCount > 0;

    return [
      {
        id: 'delete',
        label: this.translate.instant('common.delete'),
        icon: 'delete',
        disabled: this.isNew() || inUse,
      },
    ];
  });

  // ============================================================================
  // Member Management State (from service)
  // ============================================================================

  /** All members (backend + local unsaved additions, excluding local removals) */
  protected readonly allMembers = this.groupsService.allMembers;

  /** Members loading state */
  protected readonly membersLoading = this.groupsService.membersLoading;

  /** Check if there are unsaved member changes */
  protected readonly hasUnsavedMemberChanges = this.groupsService.hasUnsavedMemberChanges;

  /** Unassigned users available for adding */
  private readonly createUsers = computed(() => {
    return this.usersService.users().map(user => ({
      id: user.id,
      name: user.name,
      email: user.email,
    }));
  });

  protected readonly unassignedUsers = computed(() => {
    if (this.isNew()) {
      const localAddedIds = new Set(this.groupsService.localAddedMembers().map(member => member.userId));
      return this.createUsers().filter(user => !localAddedIds.has(user.id));
    }

    return this.groupsService.availableUnassignedUsers();
  });

  /** Dropdown options for unassigned users */
  protected readonly unassignedUserOptions = computed<DropdownOption<string>[]>(() => {
    return this.unassignedUsers().map(user => ({
      label: `${user.name} (${user.email})`,
      value: user.id,
    }));
  });

  /** Loading state for unassigned users */
  protected readonly unassignedLoading = computed(() => {
    return this.isNew() ? this.usersService.isLoading() : this.groupsService.unassignedLoading();
  });

  /** Signal for selected user in dropdown (for adding) */
  protected readonly selectedUserToAdd = signal<string | null>(null);

  // ============================================================================
  // Role Management State (from service)
  // ============================================================================

  /** All roles (backend + local unsaved additions, excluding local removals) */
  protected readonly allRoles = this.groupsService.allRoles;

  /** Roles loading state */
  protected readonly rolesLoading = this.groupsService.rolesLoading;

  /** Check if there are unsaved role changes */
  protected readonly hasUnsavedRoleChanges = this.groupsService.hasUnsavedRoleChanges;

  /** Unassigned roles available for adding */
  private readonly createRoles = computed(() => {
    return this.rolesService.roles().map(role => ({
      id: role.id,
      description: role.description,
      createdAt: role.createdAt,
      updatedAt: role.updatedAt,
    }));
  });

  protected readonly unassignedRoles = computed(() => {
    if (this.isNew()) {
      const localAddedIds = new Set(this.groupsService.localAddedRoles().map(role => role.roleId));
      return this.createRoles().filter(role => !localAddedIds.has(role.id));
    }

    return this.groupsService.availableUnassignedRoles();
  });

  /** Dropdown options for unassigned roles */
  protected readonly unassignedRoleOptions = computed<DropdownOption<string>[]>(() => {
    return this.unassignedRoles().map(role => ({
      label: role.description ? `${role.id} (${role.description})` : role.id,
      value: role.id,
    }));
  });

  /** Loading state for unassigned roles */
  protected readonly unassignedRolesLoading = computed(() => {
    return this.isNew() ? this.rolesService.isLoading() : this.groupsService.unassignedRolesLoading();
  });

  /** Signal for selected role in dropdown (for adding) */
  protected readonly selectedRoleToAdd = signal<string | null>(null);

  /** Track previous ID to detect changes */
  private previousGroupId: string | null | undefined = undefined;

  /** Badge counts for accordion headers */
  readonly memberCountBadge = computed(() => this.allMembers().length);
  readonly roleCountBadge = computed(() => this.allRoles().length);

  /** Check if form has unsaved changes */
  readonly hasUnsavedFormChanges = computed(() => {
    const current = this.formValues();
    return current.name !== this._originalFormValues.name || current.description !== this._originalFormValues.description;
  });

  /** Combined check for any pending changes */
  readonly hasPendingChanges = computed(() => {
    if (this.isNew()) {
      const formValues = this.formValues();
      const name = (formValues.name ?? '').trim();
      return name !== '';
    }
    return this.hasUnsavedFormChanges() || this.hasUnsavedMemberChanges() || this.hasUnsavedRoleChanges();
  });

  constructor() {
    wireAuditPanelAutoOpen({
      isNew: this.isNew,
      canAccess: this.canAccessAuditLog,
      isSidebarCollapsed: this.sidebarNavState.isCollapsed,
      auditPanelOpen: this.auditPanelOpen,
      breakpointObserver: this.breakpointObserver,
      destroyRef: this.destroyRef,
      injector: this.injector,
    });

    // React to route param changes: reset form and load group data.
    this.route.paramMap.pipe(takeUntilDestroyed()).subscribe(() => {
      const currentId = this.groupId();
      if (currentId !== this.previousGroupId) {
        this.previousGroupId = currentId;
        this.form.reset({ name: '', description: null });
        this._originalFormValues = { name: '', description: null };
        this.selectedUserToAdd.set(null);
        this.selectedRoleToAdd.set(null);

        if (currentId) {
          this.groupsService.selectById(currentId);
          this.groupsService.reloadMembers();
          this.groupsService.reloadRoles();
        } else {
          this.groupsService.clearSelection();
        }
      }
    });

    // Populate form when group data loads.
    toObservable(this.selectedGroup)
      .pipe(takeUntilDestroyed())
      .subscribe(group => {
        if (group && !this.isNew()) {
          this.form.patchValue({
            name: group.name,
            description: group.description,
          });
          this._originalFormValues = {
            name: group.name,
            description: group.description,
          };
        }
      });

    // Clear local state when fresh data arrives after save.
    toObservable(this.selectedGroup)
      .pipe(takeUntilDestroyed())
      .subscribe(group => {
        if (!group) return;
        const currentDataHash = `${group.memberCount}:${group.roleCount}:${group.name}`;

        if (this._pendingRefresh() && currentDataHash !== this._previousDataHash) {
          this.groupsService.clearLocalMemberChanges();
          this.groupsService.clearLocalRoleChanges();
          this._originalFormValues = {
            name: group.name,
            description: group.description,
          };
          this._pendingRefresh.set(false);
        }

        this._previousDataHash = currentDataHash;
      });
  }

  // ============================================================================
  // Member Management Methods
  // ============================================================================

  protected onUserToAddSelected(userId: string | null): void {
    if (!userId) return;

    const user = this.unassignedUsers().find(u => u.id === userId);
    if (user) {
      this.groupsService.addMemberLocally(user, false);
      this.toast.success(this.translate.instant('settings.groups.memberAddedLocal'));
    }

    this.selectedUserToAdd.set(null);
  }

  protected removeMember(member: GroupMember): void {
    this.groupsService.removeMemberLocally(member.userId);
  }

  protected isLocalMemberAddition(member: GroupMember): boolean {
    const localAdded = this.groupsService.localAddedMembers();
    return localAdded.some(m => m.userId === member.userId);
  }

  // ============================================================================
  // Role Management Methods
  // ============================================================================

  protected onRoleToAddSelected(roleId: string | null): void {
    if (!roleId) return;

    const role = this.unassignedRoles().find(r => r.id === roleId);
    if (role) {
      this.groupsService.addRoleLocally(role);
      this.toast.success(this.translate.instant('settings.groups.roleAddedLocal'));
    }

    this.selectedRoleToAdd.set(null);
  }

  protected removeRole(role: GroupRole): void {
    this.groupsService.removeRoleLocally(role.id);
  }

  protected isLocalRoleAddition(role: GroupRole): boolean {
    const localAdded = this.groupsService.localAddedRoles();
    return localAdded.some(r => r.roleId === role.id);
  }

  // ============================================================================
  // Combined Save for All Changes
  // ============================================================================

  protected saveAll(): void {
    if (this.isNew()) {
      this.createGroup();
      return;
    }

    const groupId = this.groupId();
    if (!groupId || !this.hasPendingChanges()) return;

    this.isSaving.set(true);
    const operations: Observable<unknown>[] = [];

    // Add form update if changed
    if (this.hasUnsavedFormChanges()) {
      operations.push(
        this.groupsService.update(groupId, {
          name: this.form.controls.name.value,
          description: this.form.controls.description.value ?? undefined,
        })
      );
    }

    // Add member changes if any
    if (this.hasUnsavedMemberChanges()) {
      operations.push(this.groupsService.saveMemberChanges(groupId));
    }

    // Add role changes if any
    if (this.hasUnsavedRoleChanges()) {
      operations.push(this.groupsService.saveRoleChanges(groupId));
    }

    forkJoin(operations.length > 0 ? operations : [of(null)]).subscribe({
      next: () => {
        this.isSaving.set(false);
        this.toast.success(this.translate.instant('settings.groups.saveSuccess'));
        // Set pending refresh flag - local state will be cleared when fresh data arrives
        this._pendingRefresh.set(true);
        this.groupsService.reloadSelected();
      },
      error: (err: { error?: { message?: string } }) => {
        this.isSaving.set(false);
        this.toast.error(err?.error?.message || this.translate.instant('settings.groups.saveError'));
      },
    });
  }

  private createGroup(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSaving.set(true);

    this.groupsService
      .create({
        name: this.form.controls.name.value,
        description: this.form.controls.description.value ?? undefined,
      })
      .pipe(
        switchMap(res => {
          const groupId = res.id;
          const operations: Observable<unknown>[] = [];

          if (this.hasUnsavedMemberChanges()) {
            operations.push(this.groupsService.saveMemberChanges(groupId));
          }

          if (this.hasUnsavedRoleChanges()) {
            operations.push(this.groupsService.saveRoleChanges(groupId));
          }

          if (operations.length === 0) {
            return of(res);
          }

          return forkJoin(operations).pipe(map(() => res));
        })
      )
      .subscribe({
        next: res => {
          this.isSaving.set(false);
          this.toast.success(this.translate.instant('settings.groups.saveSuccess'));
          this.groupsService.reload();
          // Navigate to edit page for newly created group
          void this.router.navigate(['/settings/groups', res.id]);
        },
        error: (err: { error?: { message?: string } }) => {
          this.isSaving.set(false);
          this.toast.error(err?.error?.message || this.translate.instant('settings.groups.saveError'));
        },
      });
  }

  // ============================================================================
  // Navigation & Actions
  // ============================================================================

  protected onCancel(): void {
    void this.router.navigate(['/settings/groups']);
  }

  /** Handle quick action click */
  protected onQuickAction(action: ProfileQuickAction): void {
    if (action.id === 'audit-log') {
      this.auditPanelOpen.set(!this.auditPanelOpen());
    }
  }

  /** Handle activity log click */
  protected onActivityLog(): void {
    this.auditPanelOpen.set(!this.auditPanelOpen());
  }

  protected onDangerAction(action: { id: string }): void {
    if (action.id === 'delete') {
      this.onDelete();
    }
  }

  private async onDelete(): Promise<void> {
    const groupId = this.groupId();
    const group = this.selectedGroup();
    if (!groupId || !group) return;

    const confirmMessage = this.translate.instant('settings.groups.deleteConfirm', {
      name: group.name,
    });
    const confirmed = await this.confirmDialog.confirm({
      title: this.translate.instant('common.delete'),
      message: confirmMessage,
      confirmText: this.translate.instant('common.delete'),
      destructive: true,
    });
    if (!confirmed) return;

    this.groupsService.delete(groupId).subscribe({
      next: () => {
        this.toast.success(this.translate.instant('settings.groups.deleteSuccess'));
        void this.router.navigate(['/settings/groups']);
      },
      error: (err: { error?: { message?: string } }) => {
        this.toast.error(err?.error?.message || this.translate.instant('settings.groups.deleteError'));
      },
    });
  }

  protected getFieldError(field: keyof typeof this.form.controls): string | null {
    return getFieldErrorUtil(this.form.controls[field], this.translate);
  }
}
